// Public changelog content for the marketing site (/changelog), rendered by
// src/site/pages/SiteChangelog. Newest entry first.
//
// To add a release, prepend an entry. Shape:
//   {
//     date: "2026-06-23",          // ISO date; formatted for display at render
//     slug: "june-23-2026",        // optional; entry anchor id (#june-23-2026)
//     version: "v0.5.6",           // optional version/meta label
//     title: "What's new in Shadow",
//     intro: "...",                // optional lead paragraph; supports **bold**
//     sections: [
//       {
//         slug: "section-title",   // optional; section anchor suffix
//         heading: "🎯 Section title",
//         big: false,              // optional — larger heading for headline features
//         body: "One-line summary.",       // optional; supports **bold**
//         items: [                         // optional sub-features (h3 + text)
//           { title: "Sub-feature", body: "Detail." },
//         ],
//         images: [                        // optional screenshots
//           { src: "https://…", alt: "…" },
//         ],
//         bullets: ["**Lead:** detail."],  // optional bullet list; supports **bold**
//       },
//     ],
//   }
//
// Anchors / email deep-links: every entry and section renders an `id` so you can link
// straight to a change from an email. The entry id is its `slug` (e.g. `june-23-2026`);
// each section id is that entry id + "-" + the section's `slug`, which keeps section ids
// unique across the page automatically:
//   https://shadowlabs.ai/changelog#june-23-2026                  (whole release)
//   https://shadowlabs.ai/changelog#june-23-2026-pre-meeting-brief (one section)
// `slug` is optional (auto-derived from the date/heading when omitted), but don't rename
// a published slug — that breaks links already sent out.

const ASSET = "https://storage.googleapis.com/shadowlabs-email-assets/email/2026-06-23";
const ASSET_0711 = "https://storage.googleapis.com/shadowlabs-email-assets/email/2026-07-11";

export const CHANGELOG = [
  {
    date: "2026-07-11",
    slug: "july-11-2026",
    version: "v0.6.18",
    title: "Shadow, rebuilt around a conversation",
    intro:
      "This is the biggest release since we launched. Shadow is no longer a set of pages you visit — " +
      "it's one conversation that knows your day. **Ask Shadow is now your home screen.** It preps you " +
      "before calls, recaps them after, writes you a brief every morning, and now runs work for you " +
      "through agents you can build yourself. Sales teams get a real two-way Salesforce, a pipeline that " +
      "keeps itself current, and coaching baked into every call.",
    sections: [
      {
        slug: "ask-shadow-home",
        heading: "💬 Ask Shadow is now Home",
        big: true,
        body:
          "The whole app is built around a conversation now. Ask Shadow anything about your meetings, " +
          "your pipeline, or your day — and your meetings live in a pane right beside it. Your prep, your " +
          "recaps, and your morning brief all arrive as messages you can reply to.",
        images: [
          { src: `${ASSET_0711}/01-ask-shadow-home.png`, alt: "Ask Shadow as the home screen" },
        ],
      },
      {
        slug: "morning-brief",
        heading: "🌅 Your Morning Brief",
        big: true,
        body:
          "Every morning at 7, Shadow writes you a brief: what's on today, which deals are slipping, who " +
          "to re-engage, and patterns it's noticing across your pipeline — then lets you act on it right " +
          "there, from jumping into a meeting to saving a follow-up as a draft.",
        images: [
          { src: `${ASSET_0711}/02-morning-brief.png`, alt: "Narrative morning brief" },
          { src: `${ASSET_0711}/02-morning-brief-2.png`, alt: "Morning brief, continued" },
        ],
      },
      {
        slug: "pre-meeting-brief",
        heading: "🧠 Pre-Meeting Brief, as a conversation",
        body:
          "Before every call, Shadow hands you a conversational brief on the room — who's attending, their " +
          "company, the account's open items, and what matters this meeting — with quote cards you can " +
          "trace straight back to the source.",
        images: [
          { src: `${ASSET_0711}/03-pre-meeting-brief.png`, alt: "Conversational pre-meeting brief" },
        ],
      },
      {
        slug: "post-meeting-brief",
        heading: "📝 Post-Meeting Brief with Update Salesforce cards",
        body:
          "After the call, the recap lands in the same thread — highlights, action items, and the agent " +
          "runs Shadow kicked off, all one reply away. Review the changes Shadow drafted on an Update " +
          "Salesforce card, edit any field inline, and push it back to your CRM without leaving the " +
          "conversation.",
        images: [
          { src: `${ASSET_0711}/04-update-salesforce-card.png`, alt: "Update Salesforce card with editable fields" },
        ],
      },
      {
        slug: "agents",
        heading: "🤖 Design your own agents",
        big: true,
        body:
          "Automate the busywork on a visual canvas. Drag together triggers — a schedule, or the end of a " +
          "meeting — and steps across your connected tools, start from a template, or just tell Shadow " +
          "what you want in chat and it builds the agent for you. Nothing goes out until you approve it.",
        images: [
          { src: `${ASSET_0711}/05-agents-canvas.png`, alt: "Designing an agent on the visual canvas" },
          { src: `${ASSET_0711}/05-agents-templates.png`, alt: "Agent templates" },
        ],
      },
      {
        slug: "salesforce",
        heading: "☁️ A real two-way Salesforce",
        big: true,
        body:
          "Shadow now mirrors your Salesforce and keeps it current. A new Deals tab and unified Accounts " +
          "view bring your pipeline into Shadow, at-risk deals surface in your brief, and you can push " +
          "accounts, people, and edited fields straight back — no tab-switching after a call.",
        images: [
          { src: `${ASSET_0711}/06-deals.png`, alt: "Deals tab" },
          { src: `${ASSET_0711}/06-accounts.png`, alt: "Unified Accounts view" },
        ],
      },
      {
        slug: "deal-qualification",
        heading: "🎯 Every deal, qualified automatically",
        big: true,
        body:
          "Shadow scores every deal against a real sales methodology — MEDDPICC for open opportunities, " +
          "BANT for early leads — pillar by pillar, marking each one strong or warm and backing it with " +
          "the exact quotes from your calls. You see where a deal is solid and where it's soft before you " +
          "walk in, and you can edit any pillar or switch frameworks to match how your team sells.",
        images: [
          { src: `${ASSET_0711}/09-deal-qualification.png`, alt: "MEDDPICC deal qualification analysis" },
        ],
      },
      {
        slug: "accounts-graph",
        heading: "🕸️ A relationship graph that builds itself",
        body:
          "Every meeting quietly adds to a map of the people and companies you deal with — pulled together " +
          "with LinkedIn photos, roles, and links, so your contacts are always up to date without you " +
          "typing a thing.",
      },
      {
        slug: "coaching",
        heading: "📈 Coaching on every call",
        body:
          "Post-call ratings now show your talk-time as a rep, so you can see when you're talking more " +
          "than listening. Action items got sharper too — Shadow double-checks each one and figures out " +
          "who actually owns it.",
        images: [{ src: `${ASSET_0711}/08-coaching.png`, alt: "Post-call rating with talk-time" }],
      },
      {
        slug: "small-updates",
        heading: "✨ Small updates",
        bullets: [
          "**Approvals everywhere:** emails, Slack messages, and CRM updates always wait for your go-ahead before sending.",
          "**Instant loads:** pages now open from cache and refresh in the background, even across app restarts.",
          "**Always up to date:** Shadow checks for updates the moment you launch it.",
          "**Reliability:** no more duplicate or zombie meeting launches, a steadier macOS dock icon, and a cleaner Windows experience.",
        ],
      },
    ],
  },
  {
    date: "2026-06-23",
    slug: "june-23-2026",
    version: "v0.5.6",
    title: "What's new in Shadow",
    intro:
      "In case you're still getting to know it: **Shadow is your on-call personal assistant for sales.** " +
      "Whether you're a founder closing your own deals or a rep running a full pipeline, Shadow joins your " +
      "calls, preps you beforehand, and keeps track of everything after, so you can focus on the conversation. " +
      "This release gives Shadow a bit more personality, makes it sharper at getting you prepared, and lets " +
      "you bring it to your whole team.",
    sections: [
      {
        slug: "new-look",
        heading: "🎨 A whole new look, and a bit of personality",
        body:
          "Shadow has a face now. We rebuilt the app on a new brand system — cleaner, calmer, and easier to " +
          "read at a glance — with a little character peeking around the edges.",
        images: [
          { src: `${ASSET}/01-new-look-1.jpg`, alt: "Updated branding" },
          { src: `${ASSET}/01-new-look-2.jpg`, alt: "Better branding" },
        ],
      },
      {
        slug: "agendas",
        heading: "🎯 Never miss a point",
        body:
          "Shadow now plans your meeting, tracks it live, and writes the notes for you, so nothing slips through.",
        items: [
          {
            title: "Just say what you want covered",
            body: "Tell Shadow the goal and it drafts the agenda items for you.",
            image: { src: `${ASSET}/02-agenda-create.jpg`, alt: "Creating agenda items" },
          },
          {
            title: "Save templates for recurring meetings",
            body: "Reuse the same structure for standups, 1:1s, and demos.",
            image: { src: `${ASSET}/02-agenda-template.jpg`, alt: "Saving agenda templates" },
          },
          {
            title: "Live agenda with auto-captured notes",
            body:
              "During the call your agenda sits in the top overlay, showing what's been covered and what's " +
              "left, with structured notes captured under each point.",
            image: { src: `${ASSET}/02-agenda-live.jpg`, alt: "Live agenda with auto-capture" },
          },
        ],
      },
      {
        slug: "pre-meeting-brief",
        heading: "🧠 Pre-Meeting Brief",
        big: true,
        body:
          "Walk into every call already knowing the room. Shadow researches each attendee and their company " +
          "beforehand, so you never start cold.",
        images: [
          { src: `${ASSET}/03-brief-person.jpg`, alt: "Meeting brief, person" },
          { src: `${ASSET}/03-brief-company.jpg`, alt: "Meeting brief, company" },
        ],
      },
      {
        slug: "memory",
        heading: "💡 Memory",
        body:
          "Shadow now carries context across your meetings, remembering what was said before and bringing it " +
          "forward when it matters. It's still early, but it's a real start toward Shadow understanding your " +
          "full history.",
        images: [{ src: `${ASSET}/04-memory.jpg`, alt: "Memory" }],
      },
      {
        slug: "teams",
        heading: "🏢 Shadow, now for teams",
        big: true,
        body:
          "Bring Shadow to your whole team. Control what's shared versus private, see what your reps are " +
          "working on, and guide them with a team-wide view of meetings and action items. (On the enterprise plan.)",
        images: [
          { src: `${ASSET}/05-team-meetings.jpg`, alt: "Team meetings" },
          { src: `${ASSET}/05-team.jpg`, alt: "Team" },
        ],
      },
      {
        slug: "small-updates",
        heading: "✨ Small updates",
        bullets: [
          "**Google Docs and Notion as knowledge sources:** paste a URL and Shadow reads the real content, not the login page.",
          "**Full transcripts:** long meetings are no longer clipped.",
          "**Join Meeting on 11 providers:** up from 3, with auto-record.",
          "**Calendar alerts one minute before:** plus a one-click reconnect banner.",
          "**Chat that keeps up:** no more \"servers are busy\" on complex queries.",
          "**Action Items revamp:** Mine and All tabs with filters.",
          "**Editable profile, smarter update prompts, and Windows fixes:** mic detection and reliable auto-update.",
        ],
      },
    ],
  },
  {
    date: "2026-06-09",
    slug: "june-9-2026",
    title: "Smarter action items, tool suggestions, and Windows support",
    intro: "We've been busy this week making Shadow smarter and more reliable. Here's what's new:",
    sections: [
      {
        slug: "smarter-action-items",
        heading: "🎯 Smarter Action Items",
        bullets: [
          "Action items from your meetings are now significantly more accurate.",
          "The \"Preparing…\" button no longer gets stuck — everything processes smoothly.",
          "Connect a new tool and Shadow automatically retries any pending action items.",
          "The meeting review page now always shows the latest action items, with stale ones cleaned up.",
        ],
      },
      {
        slug: "tool-suggestions",
        heading: "🔌 Smarter Tool Suggestions",
        bullets: [
          "\"Connect X\" prompts now only appear when a relevant integration actually exists.",
        ],
      },
      {
        slug: "windows-support",
        heading: "🪟 Windows Support",
        bullets: [
          "Shadow Desktop is now available on Windows! Download and install with a native setup experience.",
        ],
      },
      {
        slug: "better-ai-responses",
        heading: "🤖 Better AI Responses",
        bullets: [
          "The chat agent is now more context-aware, giving you more accurate answers about your meetings.",
        ],
      },
    ],
  },
  {
    date: "2026-05-26",
    slug: "may-26-2026",
    version: "v0.4.13",
    title: "Hey Shadow, smarter follow-ups",
    intro:
      "A few long-asked-for things landed this fortnight. You can call Shadow without lifting a finger, " +
      "send action items straight to Linear, and trust that long meetings don't get clipped on the way out.",
    sections: [
      {
        slug: "hey-shadow",
        heading: "🎙️ \"Hey Shadow\"",
        big: true,
        body:
          "Shadow now responds to a voice trigger. Say \"Hey Shadow\" in a call and it'll surface a card " +
          "without you ever leaving the conversation.",
      },
      {
        slug: "linear",
        heading: "📋 Linear",
        big: true,
        body:
          "Action items from your meetings can now land directly on your Linear board, with the right " +
          "assignee and project picked up automatically.",
      },
      {
        slug: "action-items",
        heading: "🎯 Action items, sharper",
        bullets: [
          "**More accurate:** action items pick up commitments more reliably and miss fewer follow-ups.",
          "**Tested in real meetings:** an expanded test bench keeps regressions out before they ship.",
        ],
      },
      {
        slug: "long-summaries",
        heading: "📝 Summaries no longer get cut off",
        body:
          "Long meetings used to occasionally end mid-sentence in the summary. Those clipped summaries " +
          "are now automatically regenerated cleanly.",
      },
      {
        slug: "small-updates",
        heading: "✨ Small updates",
        bullets: [
          "**Mid-meeting source updates apply live:** add or remove a source during a call and Shadow picks it up immediately, no refresh needed.",
          "**The chat sees the live transcript:** ask \"what just happened\" mid-meeting and you'll get a current answer, not a stale one.",
          "**Windows launch reminder:** when your mic goes hot for a call, Windows pings you to launch Shadow.",
          "**Enterprise security hardening:** stronger encryption for connected accounts, tighter scoping on enterprise integrations, and a clean account-delete that wipes everything.",
          "**\"Connect a tool\" link fix:** the prompt now opens the right page instead of refreshing.",
        ],
      },
    ],
  },
  {
    date: "2026-05-12",
    slug: "may-12-2026",
    version: "v0.4.7",
    title: "Cleaner summaries, redesigned integrations",
    intro:
      "Two surfaces you spend a lot of time in got a fresh coat of paint, and your Salesforce " +
      "extractions now actually understand your team's data.",
    sections: [
      {
        slug: "summary-redesign",
        heading: "📝 A calmer post-meeting summary",
        big: true,
        body:
          "The post-meeting view has been redesigned to be easier to skim — highlights, decisions, and " +
          "action items all land where your eye goes first.",
      },
      {
        slug: "integrations-redesign",
        heading: "🔌 Redesigned Integrations page",
        big: true,
        body:
          "Integrations now live in the main nav with a card-based layout, a count of what you've " +
          "connected, and one-click reconnect or disconnect on each.",
      },
      {
        slug: "salesforce",
        heading: "☁️ Salesforce that knows your fields",
        body:
          "Shadow now reads your org's actual Salesforce schema before extracting, so custom fields, " +
          "picklists, and your team's terminology come through correctly.",
      },
      {
        slug: "overlay-preferences",
        heading: "🪟 Overlay preferences",
        body:
          "The in-call overlay has a new preferences panel for sources and screenshare visibility, and " +
          "the title bar quietly auto-hides until you reach for it.",
      },
      {
        slug: "small-updates",
        heading: "✨ Small updates",
        bullets: [
          "**Update button, redesigned:** a quieter Update button now sits in the title bar instead of crowding the sidebar.",
          "**Connecting tools is more reliable:** \"connect\" cards no longer get stuck mid-flow.",
          "**A smoother sign-in and pricing flow** with download tracking baked in.",
          "**Slack connect success page:** cleaned up after a successful Slack connection.",
        ],
      },
    ],
  },
  {
    date: "2026-04-28",
    slug: "april-28-2026",
    version: "v0.3.24",
    title: "Faster, smarter, more reliable",
    intro:
      "A reliability and polish wave — Shadow's live moments are sharper, post-meeting summaries are " +
      "tighter, and follow-ups have a home of their own.",
    sections: [
      {
        slug: "action-items-page",
        heading: "✅ A home for action items",
        bullets: [
          "A dedicated Action Items page collects every commitment Shadow has captured across your meetings.",
          "Tick them off as you handle them — your progress is saved.",
        ],
      },
      {
        slug: "live-moments",
        heading: "⚡ Sharper live moments",
        bullets: [
          "Cards arrive noticeably faster in the live overlay.",
          "Shadow now reacts to what's just been said, not to history it has already seen.",
          "Smarter, more confident cards across the board.",
        ],
      },
      {
        slug: "summaries",
        heading: "📝 Better summaries",
        bullets: [
          "The summary prompt has been rewritten end to end for tone, structure, and accuracy.",
          "Very long transcripts now feed through in full, so you don't lose the back half of an hour-long call.",
        ],
      },
      {
        slug: "polish",
        heading: "✨ Small updates",
        bullets: [
          "Refreshed landing page in time for our Product Hunt launch.",
          "Cleaner download tracking on the marketing site.",
        ],
      },
    ],
  },
  {
    date: "2026-04-14",
    slug: "april-14-2026",
    version: "v0.3.22",
    title: "A redesigned Shadow, and Microsoft Calendar",
    intro:
      "The biggest visual update Shadow has had so far. Login, sidebar, the meeting view, and the live " +
      "overlay were all redesigned around a cleaner, calmer look. Calendar coverage doubled at the same time.",
    sections: [
      {
        slug: "redesign",
        heading: "🎨 A wall-to-wall redesign",
        big: true,
        body:
          "Login, the sidebar, the meeting view, and the live overlay all got a coordinated refresh — " +
          "tighter typography, calmer surfaces, and a lot less visual noise while you're on a call.",
      },
      {
        slug: "microsoft-calendar",
        heading: "📅 Microsoft Calendar",
        big: true,
        body:
          "Shadow now syncs Microsoft Calendar alongside Google. If your team is on Outlook, Shadow can " +
          "finally join the right meetings automatically.",
      },
      {
        slug: "live-meeting",
        heading: "🎯 The live meeting, polished",
        bullets: [
          "**A persistent meeting pill:** stays visible across pages so you can hop back to your call any time.",
          "**Meetings stay in Upcoming until they actually end** — no more mid-call jumps to Past.",
          "**Auto-update, less in your face:** the Update banner now sticks around just long enough, then steps out of the way.",
        ],
      },
      {
        slug: "small-updates",
        heading: "✨ Small updates",
        bullets: [
          "**Cleaner permissions onboarding** on macOS, with a UI explaining what each prompt is for.",
          "**Sign-in is sharper:** OAuth buttons stay clickable, and the terms-of-service row gently shakes if you skip it.",
          "**Faster meeting review pages:** the post-meeting view loads in parallel rather than waiting on each piece in turn.",
          "**Longer sign-in sessions:** your session now lasts up to 90 days before you need to sign in again.",
        ],
      },
    ],
  },
];
